import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../../../common/utils';
import { sendTrackEvent } from '../../../../common/utils/analytics-client';
import { isProcessingAllowedByPreferences } from '../../../../common/utils/item-processing';
import { Logger } from '../../../../common/utils/logger';
import { BSCIndexedDB } from '../../../../services/indexed-db-service';
import { ItemKeyCache } from '../../../../services/item-key-cache-service';
import { type AtlBrowserStorage, WebStorageType } from '../../../../services/web-storage-service';
import { CategoryStorageType } from '../../../../types';

// GET SET COOKIE ANALOGUE
export const setItemOverride =
	(
		bscStorage: AtlBrowserStorage,
		originalSetItem: (key: string, value: string) => void,
		storageType: WebStorageType,
	) =>
	(originalItemKey: string, originalItemValue: string): void => {
		const categoryStorageType =
			storageType === WebStorageType.LocalStorage
				? CategoryStorageType.LocalStorage
				: CategoryStorageType.SessionStorage;
		const storageContext = bscStorage.storage;
		let itemSetByPackage = false;
		if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
			itemSetByPackage = ItemKeyCache.wasSetByPackage(categoryStorageType, originalItemKey);
		} else {
			bscStorage.checkIfSetByPackage({
				itemKey: originalItemKey,
				isSetCallback: () => {
					itemSetByPackage = true;
				},
				isNotSetCallback: () => {
					// Note: This is async and could fail, but if the call errors out in IDB,
					// it will forcibly resolve to false here, so we're not catching the error again here ✅
					itemSetByPackage = false;
				},
			});
		}

		// `disableWebStorageConsentChecks` is read from the package global state so the
		// behaviour stays consistent with other consent-check flags (rather than being
		// threaded through props/options on every call site). When set, the override
		// always passes through to the underlying storage and only emits analytics
		// events — `AtlBrowserStorage` behaviour is preserved.
		const disableWebStorageConsentChecks =
			getBscGlobalState().disableWebStorageConsentChecks ?? false;

		if (disableWebStorageConsentChecks) {
			try {
				if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
					ItemKeyCache.markSetByPackage(categoryStorageType, originalItemKey);
					originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
				} else {
					originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
					// Track that this item was set through the package so subsequent
					// `checkIfSetByPackage` calls can confirm it was set properly.
					BSCIndexedDB.setRecord(originalItemKey);
				}
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'usedAtlBrowserStorageSetItemError',
					message: `${storageType} failed to setItem with consent checks disabled. ${e.message || ''}`,
					attributes: {
						outsidePackage: !itemSetByPackage,
						storageType,
						consentChecksDisabled: true,
					},
				});
			}
			if (!fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
				sendPackageOperationalEvent({
					action: 'usedAtlBrowserStorageSetItem',
					attributes: {
						itemKey: originalItemKey,
						outsidePackage: !itemSetByPackage,
						storageType,
						consentChecksDisabled: true,
					},
				});
			}
			let wasCheckAsync: boolean = true;
			try {
				const productName = getBscGlobalState().product;
				const {
					allowUncategorizedCookiesInOverrides,
					allowUncategorizedItemsInWebStorageOverrides,
				} = getBscGlobalState();
				// trigger the callbacks to emit analytics events but do nothing with the result.
				isProcessingAllowedByPreferences({
					itemKey: originalItemKey,
					allowedCallback: ({ wasAsync }) => {
						if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
							wasCheckAsync = wasAsync;
							sendPackageOperationalEvent({
								action: 'usedAtlBrowserStorageSetItem',
								attributes: {
									itemKey: originalItemKey,
									outsidePackage: !itemSetByPackage,
									storageType,
									wasAsync,
									consentChecksDisabled: true,
								},
							});
						}
					},
					blockedCallback: ({ itemHasCategory, itemCategory, wasAsync }) => {
						if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
							wasCheckAsync = wasAsync;
							sendPackageOperationalEvent({
								action: 'usedAtlBrowserStorageSetItem',
								attributes: {
									itemKey: originalItemKey,
									outsidePackage: !itemSetByPackage,
									storageType,
									wasAsync,
									consentChecksDisabled: true,
								},
							});
						}
						const isOverrideEventSampled = !Math.floor(Math.random() * 10);
						if (isOverrideEventSampled) {
							sendTrackEvent({
								source: 'package',
								action: 'package',
								actionSubject: 'uncategorizedWebStorageItemSet',
								attributes: {
									storageType,
									productName,
									originalItemKey,
									itemCategory: itemCategory ?? null,
									allowUncategorizedCookiesInOverrides,
									allowUncategorizedItemsInWebStorageOverrides,
									consentChecksDisabled: true,
									wasAsync,
								},
							});
						}
					},
					storageType: categoryStorageType,
					productName,
				});
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'usedAtlBrowserStorageSetItemError',
					message: `Failed to emit analytics with consent checks disabled. ${e.message || ''}`,
					attributes: {
						itemKey: originalItemKey,
						storageType,
						consentChecksDisabled: true,
						wasAsync: wasCheckAsync,
					},
				});
			}
			return;
		}

		if (itemSetByPackage) {
			try {
				// Controls have already been run, passthrough as usual
				return originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'usedAtlBrowserStorageSetItemError',
					message: `${storageType} failed to setItem. ${e.message || ''}`,
					attributes: {
						outsidePackage: false,
						storageType,
					},
				});
			}
		}

		try {
			if (!fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
				sendPackageOperationalEvent({
					action: 'usedAtlBrowserStorageSetItem',
					attributes: {
						itemKey: originalItemKey,
						outsidePackage: !itemSetByPackage,
						storageType,
					},
				});
			}
			let wasCheckAsync: boolean = true;
			try {
				const productName = getBscGlobalState().product;
				// Attempt to set a known item key that was set from outside the package
				isProcessingAllowedByPreferences({
					itemKey: originalItemKey,
					allowedCallback: ({ wasAsync }) => {
						if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
							wasCheckAsync = wasAsync;
							sendPackageOperationalEvent({
								action: 'usedAtlBrowserStorageSetItem',
								attributes: {
									itemKey: originalItemKey,
									outsidePackage: !itemSetByPackage,
									storageType,
									wasAsync,
								},
							});
						}
						originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
					},
					blockedCallback: ({ itemHasCategory, itemCategory, wasAsync }) => {
						if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
							wasCheckAsync = wasAsync;
							sendPackageOperationalEvent({
								action: 'usedAtlBrowserStorageSetItem',
								attributes: {
									itemKey: originalItemKey,
									outsidePackage: !itemSetByPackage,
									storageType,
									wasAsync,
								},
							});
						}
						const isOverrideEventSampled = !Math.floor(Math.random() * 10);
						if (isOverrideEventSampled) {
							sendTrackEvent({
								source: 'package',
								action: 'package',
								actionSubject: 'uncategorizedWebStorageItemSet',
								attributes: {
									storageType,
									productName,
									originalItemKey,
									itemCategory: itemCategory ?? null,
									wasAsync,
								},
							});
						}

						const {
							allowUncategorizedCookiesInOverrides,
							allowUncategorizedItemsInWebStorageOverrides,
						} = getBscGlobalState();

						// allowUncategorizedCookiesInOverrides impacts both web storage and cookies due to historical reasons
						const allowUncategorized =
							allowUncategorizedCookiesInOverrides || allowUncategorizedItemsInWebStorageOverrides;

						if (allowUncategorized && !itemHasCategory) {
							originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
						} else {
							return;
						}
					},
					storageType: categoryStorageType,
					productName,
				});
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'usedAtlBrowserStorageSetItemError',
					message: `Failed to set item. ${e.message || ''}`,
					attributes: {
						itemKey: originalItemKey,
						storageType,
						wasAsync: wasCheckAsync,
					},
				});
			}
		} catch (e: any) {
			Logger.errorWithOperationalEvent({
				action: 'usedAtlBrowserStorageSetItemError',
				message: `Failed to send ${storageType} setter event. ${e.message || ''}`,
			});
		}
	};
